import { Controller } from "@hotwired/stimulus";
import axios from "axios";
import PaymentMiddlewareUrlService from "../../service/payment/payment_middleware_url_service";

export default class extends Controller {
  static targets = ["price", "period", "iconPurple", "iconWhite", "planButton", "discount"];
  static values = { subscriptionType: String, isActualTrial: String, showTrial: String, activeBg: String };

  symbol = { usd: "$" };

  plansData = {};

  connect() {
    this.isYearly = false;

    const checkedInput = this.element.querySelector('input[name="btnradio"]:checked');

    if (checkedInput) {
      this.toggleButtons({ target: checkedInput });
    }

    this.updatePrices();
    this.updateIcons();
    this.updateButtonStates();
    this.loadPrices();
  }

  loadPrices() {
    axios.post("/get-all-plans").then(({ data }) => {
      this.plansData = data;
      this.renderPrices("usd");
      this.renderBusinessDiscount("usd");
    });
  }

  renderPrices(currency) {
    const sym = this.symbol[currency] ?? "$";
    this.element.querySelectorAll("[data-plan-key]").forEach((el) => {
      const planKey = el.dataset.planKey;
      const planData = this.plansData[planKey];
      if (!planData) return;

      const currencyData = planData.find((item) => item.currency === currency);
      if (!currencyData) return;

      const isYearly = planKey.endsWith("_year");
      const raw = isYearly ? currencyData.amount / 12 : currencyData.amount;
      const amount = Number(raw.toFixed(2));
      el.textContent = `${sym}${amount % 1 === 0 ? Math.round(amount) : amount}`;
    });
  }

  renderBusinessDiscount(currency) {
    // Keys "premium_business_month" and "premium_business_year" must exist in this.plansData,
    // which is populated from the /get-all-plans endpoint response
    const monthData = this.plansData["premium_business_month"]?.find((item) => item.currency === currency);
    const yearData = this.plansData["premium_business_year"]?.find((item) => item.currency === currency);

    if (!monthData || !yearData || !this.hasDiscountTarget) return;

    const yearPerMonthAmount = yearData.amount / 12;
    const discount = Math.round((monthData.amount - yearPerMonthAmount) * 100 / monthData.amount);
  
    this.discountTargets.forEach(el => {
      el.textContent = `${discount}%`;
    });
  }

  selectMonthPlan(event) {
    this.isYearly = false;
    this.updatePrices();
    this.toggleButtons(event);
    this.updateIcons();
    this.updateButtonStates();
  }

  selectAnnualPlan(event) {
    this.isYearly = true;
    this.updatePrices();
    this.toggleButtons(event);
    this.updateIcons();
    this.updateButtonStates();
  }

  updatePrices() {
    const prices = this.element.querySelectorAll("[data-plan]");

    prices.forEach((el) => {
      const isYearly = el.dataset.plan === "yearly";

      el.classList.toggle("d-none", this.isYearly ? !isYearly : isYearly);
    });

    this.periodTargets.forEach((el) => {
      el.textContent = this.isYearly ? "Year" : "Month";
    });
  }

  updateButtonStates() {
    const subscriptionType = this.subscriptionTypeValue;
    const isActualTrial = this.isActualTrialValue === "true";

    this.planButtonTargets.forEach((btn) => {
      const planType = btn.dataset.planType;
      const period = this.isYearly ? "year" : "month";
      const isMatch = subscriptionType === `${planType}_${period}`;
      const isCurrent = isMatch && (!isActualTrial || planType === "business");
      const isTrial = isMatch && isActualTrial && planType !== "business";

      btn.classList.toggle("disabled", isCurrent);
      btn.querySelector(".btn-default-content")?.classList.toggle("d-none", isCurrent || isTrial);
      btn.querySelector(".btn-current-plan")?.classList.toggle("d-none", !isCurrent);
      btn.querySelector(".btn-trial-active")?.classList.toggle("d-none", !isTrial);
    });
  }

  ignoreTooltipClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  buyPlan(event) {
    event.preventDefault();

    const btn = event.currentTarget;
    if (btn.classList.contains("disabled")) return;

    const planType = btn.dataset.planType;
    const period = this.isYearly ? "year" : "month";
    const trial = this.showTrialValue === "true";

    const subscriptionTypeMap = {
      starter_month:  PaymentMiddlewareUrlService.premiumStarterMonth,
      starter_year:   PaymentMiddlewareUrlService.premiumStarterYear,
      pro_month:      PaymentMiddlewareUrlService.premiumProMonth,
      pro_year:       PaymentMiddlewareUrlService.premiumProYear,
      business_month: PaymentMiddlewareUrlService.premiumBusinessMonth,
      business_year:  PaymentMiddlewareUrlService.premiumBusinessYear,
    };

    const subscriptionType = subscriptionTypeMap[`${planType}_${period}`];
    if (!subscriptionType) return;

    if (btn.dataset.isGuestTrial === "true") {
      document.cookie = "skip_registration_trial=1; path=/; max-age=3600";
    }

    const url = PaymentMiddlewareUrlService.getPremiumMiddlewareUrl({ subscriptionType });
    if (url) {
      window.location.href = url;
    }
  }

  toggleButtons(event) {
    const activeBg = this.activeBgValue || "primary";
    const activeText = activeBg === "white" ? "text-primary" : "text-white";
    const inactiveText = activeBg === "white" ? "text-white" : "text-primary";

    const labels = this.element.querySelectorAll(".btn-group label");
    labels.forEach(label => {
      label.classList.remove(`bg-${activeBg}`, activeText);
      label.classList.add(inactiveText);
    });

    const activeLabel = event.target.nextElementSibling;
    activeLabel.classList.add(`bg-${activeBg}`, activeText);
    activeLabel.classList.remove(inactiveText);
  }

  updateIcons() {
    const invertIcons = (this.activeBgValue || "primary") === "white";
    const showPurple = invertIcons ? this.isYearly : !this.isYearly;

    this.iconPurpleTargets.forEach(el => {
      el.classList.toggle("d-none", !showPurple);
    });

    this.iconWhiteTargets.forEach(el => {
      el.classList.toggle("d-none", showPurple);
    });
  }
}